import { handleError } from './apiUtils'
import { getConfigurationAsync, xKMSClient } from './httpMethods'
import {
  DeviceKeyMappingsApi,
  DeviceKeyMappingsRequestDto,
  DeviceKeyMappingsResponseDto,
  SearchKeyMappingsRequestDto,
  SearchKeyMappingsResponseDto,
} from './kmsapiclient'

/**
 * Searches for key mappings based on the provided request.
 *
 * @param {SearchKeyMappingsRequestDto} request - The request object containing search criteria.
 * @returns {Promise<SearchKeyMappingsResponseDto>} - A promise that resolves to the search results.
 */
export async function searchKeyMappingsAsync(
  request: SearchKeyMappingsRequestDto,
): Promise<SearchKeyMappingsResponseDto> {
  try {
    const deviceKeyMappingsApi = new DeviceKeyMappingsApi(
      getConfigurationAsync(),
    )
    const response = (
      await deviceKeyMappingsApi.permissionsSearch(xKMSClient, request)
    ).data
    return response
  } catch (error) {
    handleError(error)
    return Promise.reject(error)
  }
}

/**
 * Adds key mappings based on the provided request.
 *
 * @param {DeviceKeyMappingsRequestDto} request - The request object containing the key mappings to add.
 * @returns {Promise<DeviceKeyMappingsResponseDto>} - A promise that resolves to the key mappings response.
 */
export async function addKeyMappingsAsync(
  request: DeviceKeyMappingsRequestDto,
): Promise<DeviceKeyMappingsResponseDto> {
  try {
    const deviceKeyMappingsApi = new DeviceKeyMappingsApi(
      getConfigurationAsync(),
    )
    const response = (
      await deviceKeyMappingsApi.permissionsAdd(xKMSClient, request)
    ).data
    return response
  } catch (error) {
    handleError(error)
    return Promise.reject(error)
  }
}

/**
 * Retrieves key mappings based on the provided request.
 *
 * @param {string} id - The ID of the key mappings to retrieve.
 * @returns {Promise<DeviceKeyMappingsResponseDto>} - A promise that resolves to the key mappings response.
 */
export async function removeKeyMappingsAsync(request): Promise<string> {
  try {
    const deviceKeyMappingsApi = new DeviceKeyMappingsApi(
      getConfigurationAsync(),
    )
    const response = (
      await deviceKeyMappingsApi.permissionsDelete(request.deviceId, xKMSClient)
    ).data
    return response
  } catch (error) {
    handleError(error)
    return Promise.reject(error)
  }
}

export async function updateKeyMappingsAsync(
  request,
): Promise<DeviceKeyMappingsResponseDto> {
  try {
    const deviceKeyMappingsApi = new DeviceKeyMappingsApi(
      getConfigurationAsync(),
    )
    const response = (
      await deviceKeyMappingsApi.permissionsUpdate(
        request.deviceId,
        xKMSClient,
        request,
      )
    ).data
    return response
  } catch (error) {
    handleError(error)
    return Promise.reject(error)
  }
}

/**
 * Uploads key mappings based on the provided request.
 *
 * @param {DeviceKeyMappingsRequestDto} request - The request object containing the key mappings to upload.
 * @returns {Promise<DeviceKeyMappingsResponseDto>} - A promise that resolves to the key mappings response.
 */
export async function uploadKeyMappingsAsync(
  request,
): Promise<DeviceKeyMappingsResponseDto[]> {
  try {
    const deviceKeyMappingsApi = new DeviceKeyMappingsApi(
      getConfigurationAsync(),
    )
    const response = (
      await deviceKeyMappingsApi.permissionsBulkUpload(xKMSClient, request)
    ).data
    return response
  } catch (error) {
    handleError(error)
    return Promise.reject(error)
  }
}
